import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2 } from "@angular/core";
import { FeaturesService } from "@app2/shared/services/features.service";

@Directive({
    selector: "table[dsTable]",
})
export class DsTableDirective implements AfterViewInit {
    constructor(private el: ElementRef,
                private renderer: Renderer2,
                private featuresService: FeaturesService) {
    }

    ngAfterViewInit() {
        if (!this.featuresService.getFeatures().resizableTables) {
            return;
        }

        const table: HTMLTableElement = this.el.nativeElement;

        const originalParent: HTMLElement = table.parentElement;
        const scrollingContainer: HTMLDivElement = this.renderer.createElement("div");
        this.renderer.setStyle(scrollingContainer, "overflow", "auto");
        scrollingContainer.appendChild(table);
        originalParent.appendChild(scrollingContainer);

        // lock column widths before updating the table's overall width/layout so there isn't too much of a shift
        const tableHeaders = table.querySelectorAll("th");
        tableHeaders.forEach((node) => {
            const initWidthPx = getComputedStyle(node).width;
            this.renderer.setStyle(node, "width", initWidthPx);
        });

        this.renderer.setStyle(table, "width", "fit-content");
        this.renderer.setStyle(table, "table-layout", "fixed");

        tableHeaders.forEach((node) => {
            const colName = node.getAttribute("colName");
            if (colName) {
                const savedWidth = localStorage.getItem(colName + "-width");
                if (savedWidth) {
                    this.renderer.setStyle(node, "width", `${savedWidth}px`);
                }
            }
        });
    }
}

export const MINIMUM_COLUMN_WIDTH = 150;

@Directive({
    selector: "th[dsResizableColumn]"
})
export class DsResizableColumnDirective implements AfterViewInit, OnDestroy {
    minWidth: number;
    clickListenerCleanup: () => void;
    mouseDownListenerCleanup: () => void;

    constructor(private el: ElementRef,
                private renderer: Renderer2,
                private featuresService: FeaturesService) {
    }

    ngAfterViewInit() {
        if (!this.featuresService.getFeatures().resizableTables) {
            return;
        }

        const header: HTMLHeadElement = this.el.nativeElement;
        const colName = header.getAttribute("colName");

        const div: HTMLDivElement = this.renderer.createElement("div");
        this.renderer.addClass(div, "resize-column-marker");
        header.appendChild(div);

        // if the column is originally shorter than the predefined min width, use it as the minimum
        this.minWidth = Math.min(header.clientWidth, MINIMUM_COLUMN_WIDTH);

        this.clickListenerCleanup = this.renderer.listen(div, "click", (event: MouseEvent) => {
            // prevent click listeners, like column sort, from firing
            // it still seems to fire if you drag past the minimum size and end up on the column
            event.preventDefault();
            event.stopPropagation();
        });
        this.mouseDownListenerCleanup = this.renderer.listen(div, "mousedown", (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            const initWidth = header.clientWidth;
            const initX = event.clientX;
            let newWidth;

            const mouseMoveListenerCleanup = this.renderer.listen(document, "mousemove", (event: MouseEvent) => {
                const dX = event.clientX - initX;
                newWidth = Math.max(this.minWidth, initWidth + dX);
                this.renderer.setStyle(header, "width", `${newWidth}px`);
            });
            const mouseUpListenerCleanup = this.renderer.listen(document, "mouseup", () => {
                localStorage.setItem(colName + "-width", newWidth.toString());
                event.preventDefault();
                event.stopPropagation();

                mouseMoveListenerCleanup();
                mouseUpListenerCleanup();
            });
        });
    }

    ngOnDestroy(): void {
        this.clickListenerCleanup?.();
        this.mouseDownListenerCleanup?.();
    }
}
